import React, { useState, useEffect } from "react";

import { t } from "i18next";
import { Trans } from "react-i18next";

import BaseModal from "./BaseModal";
import ClassifierLabelsCard from "../Cards/ClassifierLabelsCard";
import { isEmpty } from "lodash";
import { validate } from "../../utils/ValidationHelpers";
import classifiersSchema from "../../schemas/classifiers.schema";
import ValidationError from "../Shared/ValidationError";

const EMPTY_LABELS = [
    { label: "", value: 1 },
    { label: "", value: 2 },
];

const ClassifierGroupModal = ({
    isVisible,
    setIsVisible,
    data,
    setData,
    updateAction,
    createAction,
}) => {
    const [labels, setLabels] = useState([]);
    const [isEdit, setIsEdit] = useState(false);

    const [isFormSubmitted, setIsFormSubmitted] = useState(false);
    const [isFormSubmitting, setIsFormSubmitting] = useState(false);

    const [labelsError, setLabelsError] = useState({});
    const [isLabelsValid, setIsLabelsValid] = useState(false);

    const [dataError, setDataError] = useState({});
    const [isDataValid, setIsDataValid] = useState(false);

    useEffect(() => {
        if (!isVisible) resetForm();
        if (isVisible) setIsEdit(!isEmpty(data));
    }, [isVisible]);

    useEffect(() => {
        extractLabels();
    }, [data]);

    useEffect(() => {
        validate(
            labels,
            classifiersSchema.ClassifierLabels,
            setIsLabelsValid,
            setLabelsError
        );
        validateValues();
    }, [labels]);

    useEffect(() => {
        validate(
            data,
            classifiersSchema.ClassifierGroup,
            setIsDataValid,
            setDataError
        );
    }, [data]);

    const getCreatePayload = () => {
        return {
            name: data.name,
            description: data.description,
            classifierLabels: labels,
        };
    };

    const getUpdatePayload = () => {
        return {
            id: data.id,
            name: data.name,
            description: data.description,
        };
    };

    const submitAction = () => {
        setIsFormSubmitted(true);
        setIsFormSubmitting(true);

        if (isDataValid && isLabelsValid) {
            isEdit
                ? updateAction(getUpdatePayload())
                : createAction(getCreatePayload());
        }

        setIsFormSubmitting(false);
    };

    const resetForm = () => {
        setData({});
        setIsEdit(false);
        setIsFormSubmitted(false);
        setIsFormSubmitting(false);
        setIsDataValid(false);
        setIsLabelsValid(false);
        setDataError({});
        setLabelsError({});
        setLabels([...EMPTY_LABELS]);
    };

    const validateValues = () => {
        const dict = {};
        for (const label of labels) {
            if (dict[label.value]) {
                setDataError((prevError) => ({
                    ...prevError,
                    labels: "Values must be unique",
                }));
                return false;
            } else dict[label.value] = true;
        }
        setDataError((prevError) => ({
            ...prevError,
            labels: "",
        }));
    };

    const extractLabels = () => {
        const newLabels = [...labels];

        setLabels(isEmpty(newLabels) ? [...EMPTY_LABELS] : newLabels);
    };

    const handleInputChange = (e) => {
        setData((prevData) => ({
            ...prevData,
            [e.target.name]: e.target.value,
        }));
    };
    const fields = [
        {
            label: (
                <label className="uppercase required">
                    <Trans i18nKey="name">name</Trans>
                </label>
            ),
            input: (
                <>
                    <input
                        className="input-element"
                        name="name"
                        type="text"
                        onChange={handleInputChange}
                        value={data?.name || ""}
                        placeholder={t("name")}
                    />
                    <ValidationError
                        errorMessage={isFormSubmitted && dataError["name"]}
                    />
                </>
            ),
        },
        {
            label: (
                <label className="uppercase">
                    <Trans i18nKey="description">Description</Trans>
                </label>
            ),
            input: (
                <>
                    <textarea
                        name="description"
                        value={data?.description || ""}
                        onChange={handleInputChange}
                        placeholder={t("descriptionPlaceholder")}
                        className="input-element textarea-element"
                    ></textarea>
                    <ValidationError
                        errorMessage={
                            isFormSubmitted && dataError["description"]
                        }
                    />
                </>
            ),
        },
        {
            hide: isEdit,
            label: (
                <label className="uppercase required">
                    <Trans i18nKey="labels">labels</Trans>
                </label>
            ),
            input: (
                <>
                    <ClassifierLabelsCard
                        labels={labels}
                        setLabels={setLabels}
                        error={labelsError}
                        isFormSubmitted={isFormSubmitted}
                    />
                    <ValidationError
                        errorMessage={isFormSubmitted && dataError["labels"]}
                    />
                </>
            ),
        },
    ];

    const renderFields = () => {
        return (
            fields &&
            fields.map((field, index) => {
                if (!field.hide) {
                    return (
                        <div className="input-group" key={index}>
                            {field.label}
                            {field.input}
                        </div>
                    );
                }
            })
        );
    };

    return (
        <>
            <BaseModal
                isVisible={isVisible}
                setIsVisible={setIsVisible}
                isSubmitting={isFormSubmitting}
                title={t(isEdit ? "editTitle" : "addTitle", {
                    entity: t("classifierGroup"),
                })}
                action={{ submitAction: submitAction }}
            >
                {renderFields()}
            </BaseModal>
        </>
    );
};

export default ClassifierGroupModal;
